import React, { useEffect, useMemo, useState } from 'react';
import {
  FAILURE_MESSAGE,
  RESET_DATE_RANGE,
  SHOW_NOTIFICATION,
  SUCCESS_KEY,
} from '../../constants';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import WorkReportLoader from './WorkReportLoader/WorkReportLoader';
import TableComponent from '../../components/Table/TableComponent';
import GenericFilterDrawer from '../../components/GenericFilter/GenericFilterDrawer';
import {
  getAllWorkReports,
  getEmployeeWorkReports,
  getManageEmployeeWorkReports,
  updateWorkReport,
} from '../../services/WorkReportService';
import { useDispatch, useSelector } from 'react-redux';
import KeycloakService from '../../services/KeycloakService';
import {
  getEmployeesOfManager,
  getUserProjects,
} from '../../services/UserService';
import {
  resetFilterDataState,
  setFilterConfig,
} from '../../modules/filter/filterAction';
import { useLocation } from 'react-router-dom';
import { checkFilterIconVisibility } from '../../components/GenericFilter/FilterIconVisibilityCheck';
import { getProjectsByName } from '../../services/ProjectService';
import { DialogBoxComponent } from '../../components/DialogBox/DialogBoxCard';
import WarningIcon from '@mui/icons-material/Warning';

const WorkReports = (props) => {
  // utilities
  const [page, setPage] = useState(1);
  const [pageNo, setPageNo] = useState(0);
  const [refreshPage, setRefreshPage] = useState(false);
  const [totalPage, setTotalPage] = useState();
  const [pageSize, setPageSize] = useState(10);
  const [loader, setLoader] = useState(false);
  const [curUserRole, setCurUserRole] = useState(localStorage.getItem('mode'));
  const [options, setOptions] = useState({});
  const [refresh, setRefresh] = React.useState(false);
  const [empNameArray, setEmpNameArray] = React.useState([]);
  const [open, setOpen] = useState(false);
  const [updatedRow, setUpdatedRow] = useState({});

  const currentUserId = KeycloakService.getUserDetail();

  const dispatch = useDispatch();

  // work reports data
  const [workReportData, setWorkReportData] = useState([]);
  const [resetFilter, setResetFilter] = useState(false);

  const filterState = useSelector((state) => state.filterState);
  const location = useLocation();
  const uniqueIds = new Set();

  const [cols, setCols] = useState([
    { field: 'employeeCode', headerName: 'Employee ID', width: 150 },
    { field: 'employeeName', headerName: 'Employee Name', width: 300 },
    { field: 'projectName', headerName: 'Project Name', width: 250 },
    {
      field: 'quarterYear',
      headerName: 'Quarter & Year',
      width: 200,
      renderCell: (params) => {
        const formattedQuarter = params.row.quarter
          ? params.row.quarter.replace(
              /(Jan|Apr|Jul|Oct)(To)(Mar|Jun|Sep|Dec)/,
              '$1 to $3',
            )
          : null;
        const year = params.row.year;
        return formattedQuarter && year
          ? `${formattedQuarter}, ${year}`
          : 'N/A';
      },
    },
    {
      field: 'reportLink',
      headerName: 'Report',
      width: 180,
      renderCell: (params) =>
        params.value ? (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            View Report
          </a>
        ) : (
          'N/A'
        ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => (params.value ? params.value : 'N/A'),
    },
    {
      field: 'sprintProductivity',
      headerName: 'Sprint Productivity',
      width: 150,
      renderCell: (params) => {
        const value = params.value;

        let color = 'error';
        let label = 'Poor';

        if (value > 0.8) {
          color = 'success';
          label = 'Excellent';
        } else if (value > 0.6) {
          color = 'warning';
          label = 'Inadequate';
        }

        return value !== null && value !== undefined ? (
          <Chip label={label} color={color} variant="outlined" />
        ) : (
          'N/A'
        );
      },
    },
    {
      field: 'quarterProductivity',
      headerName: 'Quarter Productivity',
      width: 150,
      renderCell: (params) => {
        const value = params.value;

        let color = 'error';
        let label = 'Poor';

        if (value > 0.75) {
          color = 'success';
          label = 'Excellent';
        } else if (value > 0.5) {
          color = 'warning';
          label = 'Average';
        }

        return value !== null && value !== undefined ? (
          <Chip label={label} color={color} variant="outlined" />
        ) : (
          'N/A'
        );
      },
    },
    {
      field: 'markAsReviewed',
      headerName: 'Mark as Reviewed',
      width: 170,
      renderCell: (params) => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        let currentQuarter;

        // Calculate the current quarter based on the month
        if (currentMonth >= 0 && currentMonth <= 2) {
          currentQuarter = 'JanToMar';
        } else if (currentMonth >= 3 && currentMonth <= 5) {
          currentQuarter = 'AprToJun';
        } else if (currentMonth >= 6 && currentMonth <= 8) {
          currentQuarter = 'JulyToSep';
        } else {
          currentQuarter = 'OctToDec';
        }

        const isCurrentQuarterYear =
          params.row.quarter === currentQuarter &&
          params.row.year === currentYear;

        const isReviewed = params.row.status === 'Reviewed';
        const isDraft = params.row.status === 'Draft';

        const tooltipMessage = isReviewed
          ? 'This report is already reviewed.'
          : isCurrentQuarterYear
            ? 'You can review this report once the current quarter ends.'
            : isDraft
              ? 'This report is currently in draft status.'
              : '';

        return (
          <Tooltip
            title={tooltipMessage}
            arrow
            disableHoverListener={
              !isReviewed && !isCurrentQuarterYear && !isDraft
            }
          >
            <span>
              <Button
                variant="outlined"
                onClick={() => handleMarkAsReviewedClick(params)}
                color={SUCCESS_KEY}
                sx={{ width: '8rem' }}
                disabled={isReviewed || isCurrentQuarterYear || isDraft}
              >
                {isReviewed ? 'Reviewed' : 'Review'}
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'markAsFilled',
      headerName: 'Mark as Filled',
      width: 170,
      renderCell: (params) => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        let currentQuarter;

        // Calculate the current quarter based on the month
        if (currentMonth >= 0 && currentMonth <= 2) {
          currentQuarter = 'JanToMar';
        } else if (currentMonth >= 3 && currentMonth <= 5) {
          currentQuarter = 'AprToJun';
        } else if (currentMonth >= 6 && currentMonth <= 8) {
          currentQuarter = 'JulyToSep';
        } else {
          currentQuarter = 'OctToDec';
        }

        const isCurrentQuarterYear =
          params.row.quarter === currentQuarter &&
          params.row.year === currentYear;

        const isNotReviewed = params.row.status === 'Not Reviewed';
        const isReviewed = params.row.status === 'Reviewed';

        const tooltipMessage = isNotReviewed
          ? 'This report is already marked as filled.'
          : isCurrentQuarterYear
            ? 'You can mark this report as filled once the current quarter ends.'
            : isReviewed
              ? 'This report has already been reviewed'
              : '';

        return (
          <Tooltip
            title={tooltipMessage}
            arrow
            disableHoverListener={
              !isReviewed && !isCurrentQuarterYear && !isNotReviewed
            }
          >
            <span>
              <Button
                variant="outlined"
                onClick={() => handleMarkAsReviewedClick(params)}
                color={SUCCESS_KEY}
                sx={{ width: '8rem' }}
                disabled={isReviewed || isCurrentQuarterYear || isNotReviewed}
              >
                {isReviewed
                  ? 'Reviewed'
                  : isNotReviewed
                    ? 'Not Reviewed'
                    : 'Filled'}
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
  ]);

  const filterConfiguration = {
    inputField: [
      [
        ['status', 'Status'],
        [
          ['Draft', 'Draft'],
          ['Not Reviewed', 'Not Reviewed'],
          ['Reviewed', 'Reviewed'],
        ],
      ],
      [
        ['quarter', 'Quarter'],
        [
          ['JanToMar', 'Jan to Mar'],
          ['AprToJun', 'Apr to Jun'],
          ['JulToSep', 'Jul to Sep'],
          ['OctToDec', 'Oct to Dec'],
        ],
      ],
      [
        ['year', 'Year'],
        Array.from({ length: new Date().getFullYear() - 2022 }, (_, i) => {
          const year = 2023 + i;
          return [year.toString(), year.toString()];
        }).reverse(),
      ],
    ],
    autoComplete: [
      ...(location.pathname !== '/user-work-reports' &&
      location.pathname !== '/user-manage'
        ? [['Search Employee', 'employee']]
        : []),
    ],
    dropdownField: [
      ...(location.pathname === '/user-manage'
        ? [['Search Employee', 'employee', 'name', 'id']]
        : []),
      ...(curUserRole === 'User'
        ? [['Search Project', 'project', 'projectName', 'projectId']]
        : [['Search Project', 'project', 'projectName', 'id']]),
    ],
  };

  // admin-roles
  const PERMITTED_ROLES = ['HR', 'Admin', 'Report', 'Finance', 'Project-Admin'];
  // columns to be made visible to only admins and not employees
  const empColumns = ['employeeCode', 'employeeName'];
  // columns only visible to manager
  const managerColumns = ['markAsReviewed'];
  // columns only visible to employees
  const employeeColumns = ['markAsFilled'];

  const getProjectsList = async () => {
    let projectData;
    if (curUserRole === 'User') {
      projectData = await getUserProjects(currentUserId, '', '', '', 'ACTIVE');
    } else {
      projectData = await getProjectsByName();
    }

    if (projectData.isError === false) {
      setOptions({ ...options, project: projectData.data.content });
    }
    if (projectData.status !== 200) {
      dipatchNotification(projectData.type, projectData.message);
    }
  };

  const emptyFilterData = {
    employeeName: '',
    employeeId: '',
    projectName: '',
    year: '',
    quarter: '',
    status: '',
  };

  const resetThings = () => {
    for (let arrayOfArrays of filterConfiguration.dropdownField) {
      setOptions({ ...options, [arrayOfArrays[1]]: [] });
    }
    dispatch(setFilterConfig(filterConfiguration));
    dispatch(resetFilterDataState());
    dispatch({
      type: RESET_DATE_RANGE,
    });
  };

  const pageChange = (event, value) => {
    setPageNo(value - 1);
    setPage(value);
    setRefreshPage(!refreshPage);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    setPageSize(newPageSize);
    pageChange(null, 1);
    setRefreshPage(!refreshPage);
  };

  const dipatchNotification = (typ, msg) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: typ,
        message: msg,
      },
    });
  };

  const fetchWorkReports = async () => {
    let workReportResponse;
    try {
      setLoader(true);
      if (curUserRole === 'User' && location.pathname === '/user-manage') {
        workReportResponse = await getManageEmployeeWorkReports(
          currentUserId,
          filterState,
          pageSize,
          pageNo,
        );
      } else if (curUserRole === 'User') {
        workReportResponse = await getEmployeeWorkReports(
          currentUserId,
          filterState,
          pageSize,
          pageNo,
        );
      } else {
        workReportResponse = await getAllWorkReports(
          filterState,
          pageSize,
          pageNo,
        );
      }

      if (
        workReportResponse.status === 200 &&
        workReportResponse.data?.content
      ) {
        setTotalPage(workReportResponse.data.totalPages);
        const formattedData = workReportResponse.data.content.map((report) => ({
          ...report,
          employeeName: `${report.firstName} ${report.lastName}`,
        }));
        setWorkReportData(formattedData);
      } else {
        dipatchNotification('error', FAILURE_MESSAGE);
      }
    } catch (error) {
      console.error('Error fetching work reports:', error);
      dipatchNotification('error', FAILURE_MESSAGE);
    } finally {
      setLoader(false);
    }
  };

  const getAppliersList = async () => {
    const applierList = await getEmployeesOfManager(currentUserId, '');
    if (Array.isArray(applierList.data)) {
      applierList.data.forEach((item) => {
        const employeeId = item.employeeId;
        const employeeName = item.employeeName;
        if (!uniqueIds.has(employeeId)) {
          uniqueIds.add(employeeId);
          setEmpNameArray((prev) => [
            ...prev,
            { id: `${employeeId}`, name: `${employeeName}` },
          ]);
        }
      });
    }
  };

  const handleMarkAsReviewedClick = (params) => {
    setOpen(true);
    setUpdatedRow(params);
  };

  const handleMarkAsReviewed = async (params) => {
    const payload = {
      id: params.row.id,
      employeeId: params.row.employeeId,
      quarter: params.row.quarter,
      year: params.row.year,
      projectId: params.row.projectId,
      status: `${location.pathname === '/user-manage' ? 'Reviewed' : 'Not Reviewed'}`,
    };

    const response = await updateWorkReport(payload);
    if (response.isError === false) {
      dipatchNotification(
        'success',
        `Sucessfully ${location.pathname === '/user-manage' ? 'Reviewed' : 'Filled'} Work Report`,
      );
      setOpen(false);
      fetchWorkReports();
      setUpdatedRow({});
    } else {
      dipatchNotification('error', FAILURE_MESSAGE);
    }
  };

  useEffect(() => {
    // remove the additional columns from employee-view
    if (location.pathname !== '/user-manage') {
      if (!PERMITTED_ROLES.includes(curUserRole)) {
        setCols(
          cols.filter(
            (x) =>
              !empColumns.includes(x.field) &&
              !managerColumns.includes(x.field),
          ),
        );
      } else {
        setCols(
          cols.filter(
            (x) =>
              !managerColumns.includes(x.field) &&
              !employeeColumns.includes(x.field),
          ),
        );
      }
    } else {
      setCols(cols.filter((x) => !employeeColumns.includes(x.field)));
    }
  }, []);

  const temp = useMemo(() => {
    pageChange(null, 1);
  }, [filterState]);

  useEffect(() => {
    resetThings();
    setResetFilter(true);
    if (curUserRole === 'User' && location.pathname === '/user-manage') {
      getAppliersList();
    }
  }, []);

  useEffect(() => {
    setOptions((prevOptions) => ({
      ...prevOptions,
      employee: empNameArray,
    }));
  }, [empNameArray]);

  useEffect(() => {
    if (resetFilter) {
      fetchWorkReports();
      getProjectsList();
    }
  }, [pageNo, page, refresh, resetFilter, filterState, refreshPage]);

  return (
    <>
      <div>
        <Box className="main-content-overlay">
          <Box className="main-content-div">
            {checkFilterIconVisibility(
              filterState,
              emptyFilterData,
              workReportData,
            ) && (
              <React.Fragment>
                <GenericFilterDrawer
                  options={options}
                  disableDateRange={true}
                />
              </React.Fragment>
            )}
            {loader ? (
              <WorkReportLoader />
            ) : (
              <Box>
                {workReportData?.length !== 0 ? (
                  <TableComponent
                    loader={loader}
                    TableRows={workReportData}
                    TableColumns={cols}
                    pageSize={pageSize}
                    handlePageSizeChange={handlePageSizeChange}
                    totalPage={totalPage}
                    page={page}
                    pageChange={pageChange}
                  />
                ) : (
                  <Typography
                    sx={{
                      height: '30rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    No Work Reports available
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <DialogBoxComponent
          open={open}
          handleClose={() => setOpen(false)}
          confirmText="Yes"
          cancelText="No"
          content={`Are you sure, you want to mark ${updatedRow?.row?.firstName} ${updatedRow?.row?.lastName}_${updatedRow?.row?.projectName} Work Report as ${location.pathname === '/user-manage' ? 'Reviewed' : 'Filled'}?`}
          onConfirm={() => handleMarkAsReviewed(updatedRow)}
          onCancel={() => setOpen(false)}
          icon={<WarningIcon color="warning" />}
        />
      </div>
    </>
  );
};

export default WorkReports;
